<template>
  <Skeleton
    v-if="loading && !isServerSide"
    :height="skeletonHeight"
  />
  <template v-else>
    <span v-if="error" />

    <div
      v-else
      ref="carouselContRef"
      class="carousel-container"
      :class="{'simplified-carousel': simplifiedThumbnail}"
    >
      <div
        class="align-justify title-container"
        :class="{
          'margin-bottom-m': !displayDivider,
          'margin-bottom-xs': displayDivider,
        }"
      >
        <div
          class="header-container"
          :class="{' padding-right-xs': linkName }"
        >
          <slot name="title">
            <div
              v-if="carouselTitle"
              class="h4"
            >
              {{ carouselTitle }}
            </div>
          </slot>
        </div>
        <div
          v-if="linkName"
          class="link"
        >
          <a
            class="secondary-cta"
            :href="linkUrl"
            @click="toggleEdit"
          >{{ editViewItems ? linkName : 'Done' }}
          </a>
        </div>
        <div
          v-else-if="sponsoredLabel"
          class="header-sponsored-label"
        >
          Sponsored
        </div>
      </div>
      <hr
        v-if="displayDivider"
        class="margin-bottom-s product-carousel-divider"
      >
      <Carousel
        ref="carousel"
        :list="carouselProducts"
        :number-of-items="numberOfItems"
        :ssr-size="ssrSize"
        :threshold-limit="0.9"
        :show-indicator="showIndicator"
        @carousel-next="nextClick"
        @carousel-prev="prevClick"
      >
        <template #item="slotProps">
          <div
            ref="productThumbnailRef"
            :class="{ 'remove-rvi': !editViewItems }"
          >
            <ProductThumbnail
              :product="slotProps.data"
              :show-wish-list-btn-on-image="useWishList"
              :is-simplification-on="isSimplificationOn"
              :override-price-simplification="overridePriceSimplification"
              :simplification-with-v1="simplificationWithV1"
              :simplification-non-bold-percentage-exp="simplificationNonBoldPercentageExp"
              :simplification-remove-sale-exp="simplificationRemoveSaleExp"
              :suppress-tool-tip="suppressToolTip"
              :url-template="urlTemplate"
              :hide-review-text-and-rating="true"
              :show-review-condensed="showReviewCondensed"
              :color-swatch-enhancement="colorSwatchEnhancement"
              :device-type="deviceType"
              :disable-slideshow="true"
              :image-width="imageWidth"
              :wrap-product-name="wrapProductName"
              :special-offers-badge="getSpecialOffersBadge(slotProps.data)"
              :description-alignment="productDescriptionAlignment"
              :section-spacing="sectionSpacing"
              :brand-class="brandClass"
              :display-sale-price-only="!isSimplificationOn && displaySalePriceOnly"
              :open-in-new-tab="openInNewTab"
              :lazy-load="!isServerSide"
              :skeleton="pictureSkeleton"
              :skeleton-options="skeletonOptions"
              :draggable="'false'"
              @click.stop="getProductId(slotProps.data.id, slotProps.index + 1, slotProps.data?.identifier?.assetAnalytics)"
              @wish-list-icon-clicked="(data) => wishListIconClicked(data, slotProps.data.id, slotProps.index + 1)"
            />
            <div
              v-if="!editViewItems"
              class="removeOverlay"
            >
              <button
                class="secondary button"
                @click="removeProduct(slotProps.data)"
              >
                Remove
              </button>
            </div>
          </div>
        </template>
      </Carousel>
    </div>
  </template>
</template>

<script>
/* istanbul ignore file */
import { defineAsyncComponent, computed, ref } from 'vue';
import Skeleton from '@atomic-ui/skeleton';
import { useResizeObserver } from '@vueuse/core';
import { fireTag } from '@vue-util/tagManager';
import { ProductThumbnail, useSpecialOffersBadge } from '@common-vue/product-thumbnail';
import useCarouselProduct from '../composables/useCarouselProduct';
import transformer from '../utils/Interface';
import simplifiedTransformer from '../utils/InterfaceSimplified';
import isBcom from '../utils/isBcom';

export default {
  name: 'ProductCarousel',
  components: {
    Carousel: defineAsyncComponent({
      loader: () => import('@atomic-ui/carousel'),
    }),
    ProductThumbnail,
    Skeleton,
  },
  props: {
    productids: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    products: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    numberOfItems: {
      type: Object,
      require: true,
      default: () => ({
        large: 3.5,
        medium: 4.5,
        small: 1.5,
      }),
    },
    showIndicator: {
      type: Boolean,
      require: false,
      default: false,
    },
    title: {
      type: String,
      require: false,
      default: '',
    },
    useWishList: {
      type: Boolean,
      require: false,
      default: false,
    },
    urlTemplate: {
      type: Object,
      required: false,
    },
    linkName: {
      type: String,
      require: false,
      default: '',
    },
    linkUrl: {
      type: String,
      require: false,
      default: '#',
    },
    editProducts: {
      type: Boolean,
      require: false,
      default: false,
    },
    imageWidth: {
      type: Number,
      required: false,
      default: 130,
    },
    wrapProductName: {
      type: Boolean,
      required: false,
      default: false,
    },
    restructureProduct: {
      type: Function,
      required: false,
      default: transformer,
    },
    productXapiContextParam: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    showCornerBadge: {
      type: Boolean,
      required: false,
      default: false,
    },
    productDescriptionAlignment: {
      type: String,
      required: false,
      default: () => (isBcom ? 'center' : 'left'),
      validator: (val) => ['center', 'left'].includes(val),
    },
    sectionSpacing: {
      type: String,
      required: false,
      default: () => (isBcom ? 'collapse' : 'expand'),
      validator: (val) => ['expand', 'collapse'].includes(val),
    },
    brandClass: {
      type: String,
      required: false,
      default: () => (isBcom ? '' : 'medium'),
    },
    displaySalePriceOnly: {
      type: Boolean,
      required: false,
      default: () => (!!isBcom),
    },
    displayDivider: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    openInNewTab: {
      type: Boolean,
      required: false,
      default: false,
    },
    simplifiedThumbnail: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showBopsOnlyProducts: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    filterFetchedProducts: {
      type: Function,
      default: (i) => i,
    },
    idSelectedColorMap: {
      type: Object,
      default: () => ({}),
    },
    skeletonOptions: {
      type: Object,
      default: () => {},
    },
    pictureSkeleton: {
      type: Boolean,
      required: false,
      default: false,
    },
    includeMetaData: {
      type: Boolean,
      required: false,
      default: false,
    },
    analytics: {
      type: Object,
      default: () => {},
    },
    isSimplificationOn: {
      type: Boolean,
      default: false,
    },
    overridePriceSimplification: {
      type: Boolean,
      default: false,
    },
    simplificationWithV1: {
      type: Boolean,
      default: false,
    },
    simplificationNonBoldPercentageExp: {
      type: Boolean,
      default: false,
    },
    simplificationRemoveSaleExp: {
      type: Boolean,
      default: false,
    },
    suppressToolTip: {
      type: Boolean,
      default: false,
    },
    skeletonHeight: {
      type: String,
      require: false,
      default: '400px',
    },
    isServerSide: {
      type: Boolean,
      require: false,
      default: false,
    },
    ssrSize: {
      type: String,
      require: false,
      default: '',
    },
    showReviewCondensed: {
      type: Boolean,
      required: false,
      default: false,
    },
    sponsoredLabel: {
      type: Boolean,
      required: false,
      default: false,
    },
    colorSwatchEnhancement: {
      type: Boolean,
      required: false,
      default: false,
    },
    deviceType: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: [
    'rviDoneEvent',
    'pixelPresentedEvent',
    'scrollEvent',
    'panelViewEvent',
    'linkClickEvent',
    'dataLoadStatusEvent',
    'onLeftChevronClick',
    'onRightChevronClick',
    'rviEditEvent',
    'viewAllEvent',
    'removeProductEvent',
    'wish-list-icon-clicked',
  ],
  setup(props, context) {
    const top = ref('');
    const carousel = ref();
    const carouselContRef = ref();
    /* istanbul ignore next */
    useResizeObserver(carouselContRef, () => {
      if (carouselContRef.value.querySelector('.v-product-thumbnail-image-container')) {
        const prodImgWidth = carouselContRef.value.querySelector('.v-product-thumbnail-image-container').offsetWidth;
        const imgHeight = prodImgWidth / 0.8;
        const prevBtn = carousel.value.$el.querySelector('.v-button-wrapper.prev');
        const nextBtn = carousel.value.$el.querySelector('.v-button-wrapper.next');
        const btnHeight = (prevBtn?.style.display === 'none') ? nextBtn?.offsetHeight : prevBtn?.offsetHeight;
        top.value = `${(imgHeight - btnHeight) / 2}px`;
      }
    });

    const {
      loading,
      carouselProducts,
      onMounted,
      unMounted,
      nextClick,
      prevClick,
      error,
    } = useCarouselProduct(
      props.numberOfItems,
      props.products,
      props.productids,
      context,
      props.simplifiedThumbnail ? simplifiedTransformer : props.restructureProduct,
      props.productXapiContextParam,
      props.isServerSide,
      props.ssrSize,
      props.filterFetchedProducts,
      props.idSelectedColorMap,
      props.includeMetaData,
      carouselContRef,
      props.showBopsOnlyProducts,
      isBcom,
      props.colorSwatchEnhancement,
    );
    const carouselTitle = computed(() => props.title);

    const thumbnailPadding = computed(() => (props.simplifiedThumbnail ? '8px' : undefined));
    const getProductId = (productId, position, asset_analytics = '') => {
      context.emit('linkClickEvent', productId, position);
      const {
        isProductpoolMedia = false, meta = {}, poolName = '', poolText = '',
      } = props?.analytics || {};
      const { deviceType = 'PC', regionCode = 'US' } = meta?.context || {};
      /* Excludes Editorial Carousel */
      if (isProductpoolMedia) {
        fireTag('link', {
          event_name: 'content link click',
          asset_analytics: decodeURIComponent(asset_analytics).replace(/<[^>]*>?/gm, ''),
          device: deviceType,
          country: regionCode,
          pool_name: poolName,
          pool_text: poolText.replace(/<[^>]*>?/gm, ''),
          product_id: productId,
          position,
        });
      }
    };

    const wishListIconClicked = (data, productId, position) => {
      context.emit('wish-list-icon-clicked', { ...data, productId, position });
    };

    return {
      nextClick,
      prevClick,
      carouselProducts,
      onMounted,
      loading,
      unMounted,
      error,
      carouselTitle,
      top,
      carousel,
      useResizeObserver,
      carouselContRef,
      getProductId,
      thumbnailPadding,
      wishListIconClicked,
    };
  },
  data() {
    return {
      editViewItems: true,
      discardedProducts: [],
      removeButtonTopSize: '',
    };
  },
  mounted() {
    this.removeButton = new ResizeObserver(this.updateRemoveButtonTopSize);
    this.removeButton.observe(document.body);
  },
  beforeUnmount() {
    this.removeButton.unobserve(document.body);
  },
  methods: {
    toggleEdit(event) {
      if (this.editProducts && this.linkName === 'Edit') {
        if (this.editViewItems) {
          // Emits an event with link name on edit CTA
          this.$emit('rviEditEvent', this.linkName);
        }
        this.updateRemoveButtonTopSize();
        if (!this.editViewItems) {
          // Emits an event with array of products on Done CTA
          this.$emit('rviDoneEvent', this.discardedProducts, this.carouselProducts?.length);
        }
        this.editViewItems = !this.editViewItems;
        this.discardedProducts = [];
        event.preventDefault();
        return false;
      }
      // Emits an Event on other CTA link of carousel
      this.$emit('viewAllEvent', this.linkName);
      return true;
    },
    removeProduct(removedProduct) {
      // Remove functionality for recently viewed items

      // Emits an event when product is selected to remove from carousel
      this.$emit('removeProductEvent', removedProduct);
      const newProducts = this.carouselProducts.filter(
        (product) => product.id !== removedProduct.id,
      );
      this.carouselProducts = newProducts;
      this.discardedProducts.push(removedProduct.id);
    },
    updateRemoveButtonTopSize() {
      this.removeButtonTopSize = `${(this.$refs.productThumbnailRef?.clientWidth / 0.8 - 50) / 2
        }px`;
    },
    getSpecialOffersBadge(product) {
      const { getSpecialOffersBadgeSettings } = useSpecialOffersBadge();
      const badgeText = product?.pricing?.price?.priceType?.text || '';
      const badgeData = getSpecialOffersBadgeSettings(badgeText, isBcom ? 'bcom' : 'mcom');
      return {
        ...badgeData,
        enableBadge: this.showCornerBadge && badgeData.enableBadge,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.title-container {
  align-items: baseline;
  display: flex;

  @include breakpoint(medium down) {
    padding-right: rem-calc(20);
  }

  .header-container{
    width: 90%;
  }
  .link, .header-sponsored-label {
    text-align: right;
    justify-items: center;
    white-space: nowrap;
  }
  .header-sponsored-label {
    color: $gray-1-color;
    font-size: $font-size-sm;
  }
}

.remove-rvi {
  position: relative;

  .removeOverlay {
    position: absolute;
    z-index: 1;
    text-align: center;
    // remove button willbe center of the product thumnail image
    top: #{v-bind(removeButtonTopSize)};
    left: 50%;

    transform: translate(-50%);
    text-align: center;
  }

  :deep(.picture-container) {
    opacity: 0.7;
  }
}

.carousel-container {
  .product-description {
    .rating {
      flex-flow: row wrap;
    }
  }
  .product-carousel-divider {
    border: none;
    border-top: solid 1px $gray-3-color;
  }
  :deep(.v-button-wrapper) {
    height: rem-calc(32);
    top: v-bind(top);
    bottom: auto;
  }

  :deep(a.brand-and-name), :deep(.product-description) {
    position: relative;
    display: block;

    &:focus-visible {
      outline: none;
      &:before {
        content: ' ';
        display: block;
        position: absolute;
        top: rem-calc(1);
        left: rem-calc(1);
        right: rem-calc(1);
        bottom: rem-calc(1);
        outline: auto;
        outline: -webkit-focus-ring-color solid rem-calc(1);
        outline-style: auto;
      }
    }
  }
  :deep(.bottom-overlay-wrapper) {
    position: relative;
    display: block;

    &:focus-visible {
      outline: none;
      &:before {
        content: ' ';
        display: block;
        position: absolute;
        top: 0;
        left: rem-calc(1);
        right: rem-calc(1);
        bottom: 0;
        outline: auto;
        outline: -webkit-focus-ring-color solid rem-calc(1);
        outline-style: auto;
      }
    }
  }
}
</style>

<style lang="scss" >
.simplified-carousel {
  .product-description {
    .expand {
      .pricing {
        margin-top: v-bind(thumbnailPadding);
      }
    }
  }
}

</style>

<style lang="scss" brand="bcom">
  .carousel-container {
    .product-description {
      .price-reg.discount {
        color: $black;
      }
    }
     .price-simplification .discount {
         color: $black;
    }
  }
</style>
